/**
 * Kitchen fridge listing spec (Wave 2 kitchen fridge unified).
 *
 * Canonical id: `fridge`. Legacy shape/inverter subs are aliases for reads/backfill.
 * Required attrs: `fridge_door_type`, `appliance_inverter_type`, `fridge_smart_type`,
 * `capacity_liters` (optional when door type is `mini`).
 * `chest-freezers` stays a separate subcategory.
 */

import {
  composeListingTitleFromSlotsWithIdentity,
  inverterTitleToken,
  smartTitleToken,
  type ListingTitleIdentity,
} from "@/lib/listing/listingTitleTokens";
import {
  inferInverterTypeFromTitle,
  isApplianceInverterType,
  pushApplianceInverterInvalidIssue,
  pushApplianceInverterRequiredIssue,
  type ApplianceInverterType,
} from "@/lib/listing/applianceInverterListingSpec";
import { asListingSpecAttributes } from "@/lib/listing/listingAttributesJson";
import type {
  ListingSpecAttributes,
  ListingSpecValidationIssue,
} from "@/lib/listing/specRequirements";

export const FRIDGE_SMART_TYPES = ["smart", "non_smart"] as const;
export type FridgeSmartType = (typeof FRIDGE_SMART_TYPES)[number];

export const FRIDGE_DOOR_TYPES = [
  "side_by_side",
  "top_bottom",
  "single",
  "mini",
] as const;
export type FridgeDoorType = (typeof FRIDGE_DOOR_TYPES)[number];

export const CANONICAL_FRIDGE_SUBCATEGORY_ID = "fridge" as const;

export const LEGACY_FRIDGE_SUBCATEGORY_IDS = [
  "standing-fridge",
  "mini-fridge",
  "inverter-fridge",
  "side-by-side-fridge",
] as const;

export type LegacyFridgeSubcategoryId =
  (typeof LEGACY_FRIDGE_SUBCATEGORY_IDS)[number];

export const FRIDGE_SUBCATEGORY_IDS = [
  CANONICAL_FRIDGE_SUBCATEGORY_ID,
  ...LEGACY_FRIDGE_SUBCATEGORY_IDS,
] as const;

export type FridgeSubcategoryId = (typeof FRIDGE_SUBCATEGORY_IDS)[number];

export const FRIDGE_CAPACITY_LITERS_FULL_MIN = 20;
export const FRIDGE_CAPACITY_LITERS_FULL_MAX = 2000;
export const FRIDGE_CAPACITY_LITERS_MINI_MIN = 10;
export const FRIDGE_CAPACITY_LITERS_MINI_MAX = 400;

export type LegacyFridgeSubcategoryMapping = {
  legacyId: string;
  canonicalId: typeof CANONICAL_FRIDGE_SUBCATEGORY_ID;
  fridgeDoorType: FridgeDoorType | "from_title";
  applianceInverterType: ApplianceInverterType | "from_title";
};

/** Locked pivot (D1 rev): legacy shape subs → `fridge` + door type default. */
export const LEGACY_FRIDGE_SUBCATEGORY_MAP: readonly LegacyFridgeSubcategoryMapping[] =
  [
    {
      legacyId: "standing-fridge",
      canonicalId: CANONICAL_FRIDGE_SUBCATEGORY_ID,
      fridgeDoorType: "single",
      applianceInverterType: "from_title",
    },
    {
      legacyId: "mini-fridge",
      canonicalId: CANONICAL_FRIDGE_SUBCATEGORY_ID,
      fridgeDoorType: "mini",
      applianceInverterType: "non_inverter",
    },
    {
      legacyId: "inverter-fridge",
      canonicalId: CANONICAL_FRIDGE_SUBCATEGORY_ID,
      fridgeDoorType: "from_title",
      applianceInverterType: "inverter",
    },
    {
      legacyId: "side-by-side-fridge",
      canonicalId: CANONICAL_FRIDGE_SUBCATEGORY_ID,
      fridgeDoorType: "side_by_side",
      applianceInverterType: "from_title",
    },
  ] as const;

const mapByLegacyId = new Map(
  LEGACY_FRIDGE_SUBCATEGORY_MAP.map((row) => [row.legacyId, row]),
);

export type FridgeSpecFormState = {
  fridgeDoorType: FridgeDoorType | "";
  capacityLiters: number | "";
  applianceInverterType: ApplianceInverterType | "";
  fridgeSmartType: FridgeSmartType | "";
};

export function defaultFridgeSpecFormState(): FridgeSpecFormState {
  return {
    fridgeDoorType: "",
    capacityLiters: "",
    applianceInverterType: "",
    fridgeSmartType: "",
  };
}

export function isFridgeSmartType(value: unknown): value is FridgeSmartType {
  return (
    typeof value === "string" &&
    (FRIDGE_SMART_TYPES as readonly string[]).includes(value)
  );
}

export function isFridgeDoorType(value: unknown): value is FridgeDoorType {
  return (
    typeof value === "string" &&
    (FRIDGE_DOOR_TYPES as readonly string[]).includes(value)
  );
}

export function isFridgeSubcategory(
  subcategoryId: string | null | undefined,
): subcategoryId is FridgeSubcategoryId {
  return (
    typeof subcategoryId === "string" &&
    (FRIDGE_SUBCATEGORY_IDS as readonly string[]).includes(subcategoryId)
  );
}

export function isCanonicalFridgeSubcategory(
  subcategoryId: string | null | undefined,
): boolean {
  return subcategoryId === CANONICAL_FRIDGE_SUBCATEGORY_ID;
}

export function resolveCanonicalFridgeSubcategoryId(
  subcategoryId: string | null | undefined,
): string | null {
  if (!subcategoryId) return null;
  if (subcategoryId === CANONICAL_FRIDGE_SUBCATEGORY_ID) {
    return CANONICAL_FRIDGE_SUBCATEGORY_ID;
  }
  return mapByLegacyId.get(subcategoryId)?.canonicalId ?? null;
}

export function getLegacyFridgeSubcategoryMapping(
  subcategoryId: string | null | undefined,
): LegacyFridgeSubcategoryMapping | null {
  if (!subcategoryId) return null;
  return mapByLegacyId.get(subcategoryId) ?? null;
}

export function getFridgeSpecSectionLabel(): string {
  return "Fridge details";
}

export function formatFridgeDoorTypeLabel(value: FridgeDoorType): string {
  switch (value) {
    case "side_by_side":
      return "Side-by-side";
    case "top_bottom":
      return "Top & bottom";
    case "single":
      return "Single door";
    case "mini":
      return "Mini";
    default:
      return value;
  }
}

export function formatFridgeSmartTitleWord(
  value: FridgeSmartType,
): "Smart" | "" {
  return smartTitleToken(value);
}

const SIDE_BY_SIDE_TITLE_SIGNAL = /side[\s-]?by[\s-]?side/i;
const TOP_BOTTOM_TITLE_SIGNAL = /top[\s-]?(?:&|and)[\s-]?bottom/i;
const MINI_TITLE_SIGNAL = /\bmini\b/i;

/** Infer door layout from listing title (backfill + legacy hydration). */
export function inferFridgeDoorTypeFromTitle(
  title: string | null | undefined,
): FridgeDoorType {
  const t = String(title ?? "");
  if (MINI_TITLE_SIGNAL.test(t)) return "mini";
  if (SIDE_BY_SIDE_TITLE_SIGNAL.test(t)) return "side_by_side";
  if (TOP_BOTTOM_TITLE_SIGNAL.test(t)) return "top_bottom";
  return "single";
}

/** @deprecated Use inferFridgeDoorTypeFromTitle — kept for backfill script naming. */
export function inferSideBySideFromTitle(
  title: string | null | undefined,
): boolean {
  return inferFridgeDoorTypeFromTitle(title) === "side_by_side";
}

export function inferFridgeSmartTypeFromTitle(
  title: string | null | undefined,
): FridgeSmartType {
  const t = String(title ?? "").toLowerCase();
  if (
    /\bsmart\b/.test(t) ||
    /\bwifi\b/.test(t) ||
    /\bwi-?fi\b/.test(t) ||
    /\bapp\b/.test(t)
  ) {
    return "smart";
  }
  return "non_smart";
}

export function parseCapacityLitersFromTitle(
  title: string | null | undefined,
): number | null {
  const t = String(title ?? "");
  const match =
    t.match(/(\d+(?:\.\d+)?)\s*(?:l|litre|litres|liter|liters)\b/i) ??
    t.match(/\b(\d+(?:\.\d+)?)\s*l\b/i);
  if (!match?.[1]) return null;
  const n = Number(match[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
}

export function resolveFridgeDoorTypeForLegacySubcategory(
  subcategoryId: string | null | undefined,
  title?: string | null,
): FridgeDoorType | null {
  const mapped = getLegacyFridgeSubcategoryMapping(subcategoryId);
  if (!mapped) {
    if (subcategoryId === CANONICAL_FRIDGE_SUBCATEGORY_ID) return null;
    return null;
  }
  if (mapped.fridgeDoorType === "from_title") {
    return inferFridgeDoorTypeFromTitle(title);
  }
  const fromTitle = inferFridgeDoorTypeFromTitle(title);
  if (mapped.fridgeDoorType === "mini") return "mini";
  if (fromTitle === "side_by_side" || fromTitle === "top_bottom") {
    return fromTitle;
  }
  return mapped.fridgeDoorType;
}

export function resolveFridgeAttrsForLegacySubcategory(
  subcategoryId: string | null | undefined,
  title?: string | null,
): {
  targetSubcategoryId: typeof CANONICAL_FRIDGE_SUBCATEGORY_ID;
  fridgeDoorType: FridgeDoorType;
  applianceInverterType: ApplianceInverterType;
  fridgeSmartType: FridgeSmartType;
  capacityLiters: number | null;
} | null {
  const canonical = resolveCanonicalFridgeSubcategoryId(subcategoryId);
  if (!canonical) return null;

  const mapped = getLegacyFridgeSubcategoryMapping(subcategoryId);
  const fridgeDoorType =
    resolveFridgeDoorTypeForLegacySubcategory(subcategoryId, title) ??
    inferFridgeDoorTypeFromTitle(title);

  let applianceInverterType: ApplianceInverterType;
  if (mapped?.applianceInverterType === "inverter") {
    applianceInverterType = "inverter";
  } else if (mapped?.applianceInverterType === "non_inverter") {
    applianceInverterType = "non_inverter";
  } else {
    applianceInverterType = inferInverterTypeFromTitle(title);
  }

  return {
    targetSubcategoryId: CANONICAL_FRIDGE_SUBCATEGORY_ID,
    fridgeDoorType,
    applianceInverterType,
    fridgeSmartType: inferFridgeSmartTypeFromTitle(title),
    capacityLiters: parseCapacityLitersFromTitle(title),
  };
}

/** @deprecated Use resolveFridgeAttrsForLegacySubcategory */
export function resolveFridgeAttrsForLegacyInverterFridge(
  title?: string | null,
) {
  return resolveFridgeAttrsForLegacySubcategory("inverter-fridge", title);
}

/** @deprecated Use inferFridgeDoorTypeFromTitle + resolveFridgeAttrsForLegacySubcategory */
export function inferFridgeSubcategoryFromLegacyInverterFridge(
  title: string | null | undefined,
): typeof CANONICAL_FRIDGE_SUBCATEGORY_ID {
  void title;
  return CANONICAL_FRIDGE_SUBCATEGORY_ID;
}

function formatLitersForTitle(liters: number): string {
  return `${Number.isInteger(liters) ? liters : liters}L`;
}

function pushFridgeSmartRequiredIssue(
  issues: ListingSpecValidationIssue[],
  message = "Choose smart or non-smart.",
): void {
  issues.push({
    key: "fridge_smart_type",
    code: "required",
    message,
  });
}

function pushFridgeSmartInvalidIssue(
  issues: ListingSpecValidationIssue[],
  message = "Smart type must be smart or non-smart.",
): void {
  issues.push({
    key: "fridge_smart_type",
    code: "invalid_enum",
    message,
  });
}

function pushFridgeDoorRequiredIssue(
  issues: ListingSpecValidationIssue[],
  message = "Choose a door type.",
): void {
  issues.push({
    key: "fridge_door_type",
    code: "required",
    message,
  });
}

function pushFridgeDoorInvalidIssue(
  issues: ListingSpecValidationIssue[],
  message = "Door type must be side-by-side, top & bottom, single door, or mini.",
): void {
  issues.push({
    key: "fridge_door_type",
    code: "invalid_enum",
    message,
  });
}

function pushCapacityLitersRequiredIssue(
  issues: ListingSpecValidationIssue[],
  message = "Fridge capacity (liters) is required.",
): void {
  issues.push({
    key: "capacity_liters",
    code: "required",
    message,
  });
}

function validateCapacityLitersValue(
  liters: unknown,
  doorType: FridgeDoorType | null,
  issues: ListingSpecValidationIssue[],
): void {
  const isMini = doorType === "mini";
  const min = isMini
    ? FRIDGE_CAPACITY_LITERS_MINI_MIN
    : FRIDGE_CAPACITY_LITERS_FULL_MIN;
  const max = isMini
    ? FRIDGE_CAPACITY_LITERS_MINI_MAX
    : FRIDGE_CAPACITY_LITERS_FULL_MAX;

  if (liters === undefined || liters === null || liters === "") {
    if (!isMini) pushCapacityLitersRequiredIssue(issues);
    return;
  }
  if (typeof liters !== "number" || !Number.isFinite(liters)) {
    issues.push({
      key: "capacity_liters",
      code: "invalid_type",
      message: "Capacity must be a number.",
    });
    return;
  }
  if (liters < min || liters > max) {
    issues.push({
      key: "capacity_liters",
      code: "out_of_range",
      message: `Capacity must be between ${min} and ${max} liters.`,
    });
  }
}

export function validateStoredFridgeAttributes(
  _subcategoryId: string,
  attributes: ListingSpecAttributes | Record<string, unknown>,
): ListingSpecValidationIssue[] {
  const issues: ListingSpecValidationIssue[] = [];
  const attrs = attributes as ListingSpecAttributes;

  const door = attrs.fridge_door_type;
  if (door === undefined || door === null || door === "") {
    pushFridgeDoorRequiredIssue(issues);
  } else if (!isFridgeDoorType(door)) {
    pushFridgeDoorInvalidIssue(issues);
  }

  const doorType = isFridgeDoorType(door) ? door : null;

  validateCapacityLitersValue(attrs.capacity_liters, doorType, issues);

  const inverter = attrs.appliance_inverter_type;
  if (inverter === undefined || inverter === null || inverter === "") {
    pushApplianceInverterRequiredIssue(issues);
  } else if (!isApplianceInverterType(inverter)) {
    pushApplianceInverterInvalidIssue(issues);
  }

  const smart = attrs.fridge_smart_type;
  if (smart === undefined || smart === null || smart === "") {
    pushFridgeSmartRequiredIssue(issues);
  } else if (!isFridgeSmartType(smart)) {
    pushFridgeSmartInvalidIssue(issues);
  }

  return issues;
}

function doorTypeTitleSuffix(doorType: FridgeDoorType): string {
  switch (doorType) {
    case "side_by_side":
      return "Side-by-Side Fridge";
    case "top_bottom":
      return "Top & Bottom Fridge";
    case "mini":
      return "Mini Fridge";
    case "single":
    default:
      return "Fridge";
  }
}

/** Locked title canon: capacity → Inverter? → Smart? → brand? → door/product. */
export function composeFridgeTitleFromAttributes(
  attrs: ListingSpecAttributes,
  identity?: ListingTitleIdentity | null,
): string {
  const door = attrs.fridge_door_type;
  const inverter = attrs.appliance_inverter_type;
  const smart = attrs.fridge_smart_type;
  const liters = attrs.capacity_liters;

  if (!isFridgeDoorType(door)) return "";

  if (door === "mini") {
    return composeListingTitleFromSlotsWithIdentity(
      {
        capacity:
          typeof liters === "number" && Number.isFinite(liters)
            ? formatLitersForTitle(liters)
            : undefined,
        inverter: isApplianceInverterType(inverter)
          ? inverterTitleToken(inverter)
          : undefined,
        smart: isFridgeSmartType(smart) ? smartTitleToken(smart) : undefined,
        product: doorTypeTitleSuffix(door),
      },
      identity,
    );
  }

  if (typeof liters !== "number" || !Number.isFinite(liters)) return "";
  if (!isApplianceInverterType(inverter)) return "";
  if (!isFridgeSmartType(smart)) return "";

  return composeListingTitleFromSlotsWithIdentity(
    {
      capacity: formatLitersForTitle(liters),
      inverter: inverterTitleToken(inverter),
      smart: smartTitleToken(smart),
      product: doorTypeTitleSuffix(door),
    },
    identity,
  );
}

export function buildFridgeAttributesFromForm(
  form: FridgeSpecFormState,
): ListingSpecAttributes {
  const out: ListingSpecAttributes = {};
  if (isFridgeDoorType(form.fridgeDoorType)) {
    out.fridge_door_type = form.fridgeDoorType;
  }
  if (
    typeof form.capacityLiters === "number" &&
    Number.isFinite(form.capacityLiters)
  ) {
    out.capacity_liters = form.capacityLiters;
  }
  if (isApplianceInverterType(form.applianceInverterType)) {
    out.appliance_inverter_type = form.applianceInverterType;
  }
  if (isFridgeSmartType(form.fridgeSmartType)) {
    out.fridge_smart_type = form.fridgeSmartType;
  }
  return out;
}

export type FridgeFormHydrateOptions = {
  subcategoryId?: string | null;
  title?: string | null;
};

export function fridgeFormFromAttributes(
  attributes: unknown,
  options?: FridgeFormHydrateOptions,
): FridgeSpecFormState {
  const base = defaultFridgeSpecFormState();
  const attrs = asListingSpecAttributes(attributes);

  let fridgeDoorType: FridgeDoorType | "" = isFridgeDoorType(
    attrs.fridge_door_type,
  )
    ? attrs.fridge_door_type
    : base.fridgeDoorType;

  let capacityLiters: number | "" = base.capacityLiters;
  if (
    typeof attrs.capacity_liters === "number" &&
    Number.isFinite(attrs.capacity_liters)
  ) {
    capacityLiters = attrs.capacity_liters;
  } else {
    const fromTitle = parseCapacityLitersFromTitle(options?.title);
    if (fromTitle !== null) capacityLiters = fromTitle;
  }

  let applianceInverterType: ApplianceInverterType | "" =
    isApplianceInverterType(attrs.appliance_inverter_type)
      ? attrs.appliance_inverter_type
      : base.applianceInverterType;
  let fridgeSmartType: FridgeSmartType | "" = isFridgeSmartType(
    attrs.fridge_smart_type,
  )
    ? attrs.fridge_smart_type
    : base.fridgeSmartType;

  if (!fridgeDoorType && options?.subcategoryId) {
    const resolved = resolveFridgeDoorTypeForLegacySubcategory(
      options.subcategoryId,
      options.title,
    );
    if (resolved) fridgeDoorType = resolved;
  }
  if (!fridgeDoorType && options?.title) {
    fridgeDoorType = inferFridgeDoorTypeFromTitle(options.title);
  }

  if (!applianceInverterType && options?.subcategoryId) {
    const legacy = resolveFridgeAttrsForLegacySubcategory(
      options.subcategoryId,
      options.title,
    );
    if (legacy) applianceInverterType = legacy.applianceInverterType;
  }
  if (!applianceInverterType && options?.title) {
    applianceInverterType = inferInverterTypeFromTitle(options.title);
  }
  if (!fridgeSmartType && options?.title) {
    fridgeSmartType = inferFridgeSmartTypeFromTitle(options.title);
  }

  return {
    fridgeDoorType,
    capacityLiters,
    applianceInverterType,
    fridgeSmartType,
  };
}

export function getFridgeSpecFormValidationMessage(
  form: FridgeSpecFormState,
): string | undefined {
  const issues = validateStoredFridgeAttributes(
    CANONICAL_FRIDGE_SUBCATEGORY_ID,
    buildFridgeAttributesFromForm(form),
  );
  return issues[0]?.message;
}
